import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import ReactModal from 'react-modal';

import { Layout } from "@layouts";
import { Icon, Image } from "@atoms";
import { Card } from "@molecules";
import { Button as SButton } from "@shadcn";
import benefitsVideo from "../../../assets/benefits_carousel.mp4";
import { rewardsData } from "./rewards-data";
import { perksData } from "./perks-data";
import { PerkTypes, RewardTypes, PagePaths } from "../../../types";
import { styles as globalStyles } from "../../../styles/styles";
import { ItemDetails } from "./item-details";
import { InfoDetailsModalType } from "./item-details";

const HERO_SECTION_HEIGHT = 288;

export type PerksPageTemplateProps = {
  selectedRewards: any;
  selectedPerks: any;
  onClickReward: (type: RewardTypes) => void;
  onClickPerk: (type: PerkTypes) => void;
  isInfoModalOpen: boolean;
  openInfoModal: (type: RewardTypes | PerkTypes) => void;
  closeInfoModal: () => void;
  closeAndAccept: () => void;
  selectedItemType: RewardTypes | PerkTypes | null;
  isSelectedItemEnabled: boolean;
  modalType: InfoDetailsModalType;
  navigateToPage: (to: string, index?: number) => void;
};

export const PerksPageTemplate = ({
  selectedRewards,
  selectedPerks,
  onClickReward,
  onClickPerk,
  isInfoModalOpen,
  openInfoModal,
  closeInfoModal,
  closeAndAccept,
  selectedItemType,
  isSelectedItemEnabled,
  modalType,
  navigateToPage
}: PerksPageTemplateProps): JSX.Element => {
  const { t } = useTranslation();

  useEffect(() => {
  }, []);

  return (
    <Layout
      variant="dashboard"
      header={t("perks.header")}
      subheader={
        <div className="page-subtitle mt-2">
          {t("perks.subheader")}
        </div>
      }
    >
      <div className="flex flex-col justify-between w-full">
        <div className="flex flex-col pt-4 w-full max-w-5xl gap-6">
          <div
            className="flex flex-col md:flex-row items-center md:items-start gap-6 max-w-5xl border border-neutral-light rounded-2xl p-6 bg-white"
            style={{ height: HERO_SECTION_HEIGHT, minHeight: HERO_SECTION_HEIGHT, maxHeight: HERO_SECTION_HEIGHT }}
          >
            <video
              src={benefitsVideo}
              className="w-40 md:w-56 lg:w-64 max-w-full h-auto rounded-xl"
              autoPlay
              loop
              muted
              playsInline
            />
            <div className="flex flex-col text-left space-y-3 pl-4">
              <p className="text-sm font-semibold text-neutral-mediumDark uppercase tracking-wide">
                {/* Who says benefits are boring? */}
                {t("perks.hero.kicker")}
                {/* Fresh & flexible, built for today's teams. */}
              </p>
              <h3 className="text-neutral-darkest text-2xl font-semibold">
                {/* So many options! */}
                {/* Modern benefits, made simple. */}
                {t("perks.hero.title")}
              </h3>
              <p className="text-neutral-dark text-base font-normal pb-2">
                {/* Parachute gives your team many different perks to improve their life. Finally, benefits your team will actually use. */}
                {t("perks.hero.description")}
                {/* Customize your program with rewards, allowances, and celebrations that make your team feel supported every day. */}
              </p>
            </div>
          </div>
          <div>
            <div className="section-title pl-1">
              {t("perks.sections.active")}
            </div>
            <div className="flex flex-row flex-wrap pt-6 w-full">
              {perksData.filter(item => item.includedInPackage === true).map((perk: any, index: number) => 
                <div className="pr-5 pb-1" key={index}>
                  <Card
                    variant="perk"
                    onClick={() => onClickPerk(perk.type)}
                    title={t(`perks.items.${perk.type}.fullTitle`)}
                    text={t(`perks.items.${perk.type}.description`)}
                    icon={<Icon variant={perk.icon} size={24} />}
                    isSelected={selectedPerks[perk.type] === true}
                    isAvailable={perk.isAvailable}
                    includedInPackage={perk.includedInPackage}
                    isBeta={perk.isBeta}
                  />
                </div>
              )}
            </div>
          </div>
          <div>
            <div className="section-title pl-1">
              {t("perks.sections.addMore")}
            </div>
            <div className="flex flex-row flex-wrap pt-6 w-full">
              {perksData.filter(item => item.includedInPackage === false).map((perk: any, index: number) => 
                <div className="pr-5 pb-5" key={index}>
                  <Card
                    variant="perk"
                    onClick={() => onClickPerk(perk.type)}
                    title={t(`perks.items.${perk.type}.title`)}
                    text={t(`perks.items.${perk.type}.description`)}
                    icon={<Icon variant={perk.icon} size={24} />}
                    isSelected={selectedPerks[perk.type] === true}
                    isAvailable={perk.isAvailable}
                    includedInPackage={perk.includedInPackage}
                  />
                </div>
              )}
            </div>          
          </div>
          <div className="z-10">
            <ReactModal
                isOpen={isInfoModalOpen}
                onRequestClose={closeInfoModal}
                style={{ content: {...globalStyles.generalModal.content, width: 480}, overlay: globalStyles.generalModal.overlay }}
                shouldCloseOnOverlayClick={true}
                ariaHideApp={false}
              >
                <ItemDetails 
                  closeInfoModal={closeInfoModal}
                  closeAndAccept={closeAndAccept}
                  itemData={selectedItemType}
                  isSelectedItemEnabled={isSelectedItemEnabled}
                  modalType={modalType}
                />
              </ReactModal>
          </div>  
        </div>         
      </div>
    </Layout>
  );
};
